import React, { useEffect, useState } from 'react'
import { SunIcon, MoonIcon, SunMoonIcon } from '../icons'

type ThemeMode = 'light' | 'dark' | 'system'

type Props = {
    lightLabel: string
    darkLabel: string
    systemLabel: string
    showLabel?: boolean
}

export const ThemeToggle = (props: Props) => {
    const { lightLabel, darkLabel, systemLabel, showLabel = false } = props
    const [themeMode, setThemeMode] = useState<ThemeMode>('system')

    const applyTheme = (mode: ThemeMode) => {
        let shouldBeDark = false

        if (mode === 'system') {
            shouldBeDark = window.matchMedia('(prefers-color-scheme: dark)').matches
        } else {
            shouldBeDark = mode === 'dark'
        }

        if (shouldBeDark) {
            document.documentElement.classList.add('dark')
        } else {
            document.documentElement.classList.remove('dark')
        }
    }

    useEffect(() => {
        // Check initial theme from localStorage or default to system
        const storedTheme = localStorage.getItem('theme') as ThemeMode | null
        const initialMode: ThemeMode = storedTheme || 'system'
        setThemeMode(initialMode)
        applyTheme(initialMode)

        // Listen for system theme changes when in system mode
        const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
        const handleChange = () => {
            if (themeMode === 'system') {
                applyTheme('system')
            }
        }

        mediaQuery.addEventListener('change', handleChange)
        return () => mediaQuery.removeEventListener('change', handleChange)
    }, [themeMode])

    const toggleTheme = () => {
        // Cycle through: light → dark → system → light
        const modes: ThemeMode[] = ['light', 'dark', 'system']
        const currentIndex = modes.indexOf(themeMode)
        const nextMode = modes[(currentIndex + 1) % modes.length]

        setThemeMode(nextMode)
        localStorage.setItem('theme', nextMode)
        applyTheme(nextMode)
    }

    const getIcon = () => {
        switch (themeMode) {
            case 'light':
                return <SunIcon className="w-5 h-5 text-gray-700 dark:text-gray-300" />
            case 'dark':
                return <MoonIcon className="w-5 h-5 text-gray-700 dark:text-gray-300" />
            case 'system':
                return <SunMoonIcon className="w-5 h-5 text-gray-700 dark:text-gray-300" />
        }
    }

    const getCurrentModeDisplay = () => {
        switch (themeMode) {
            case 'light':
                return 'Light Mode'
            case 'dark':
                return 'Dark Mode'
            case 'system':
                return 'System'
        }
    }

    const getNextLabel = () => {
        switch (themeMode) {
            case 'light':
                return darkLabel
            case 'dark':
                return systemLabel
            case 'system':
                return lightLabel
        }
    }

    return (
        <button
            onClick={toggleTheme}
            className={`rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors ${
                showLabel ? 'flex items-center gap-3 px-3 py-2 w-full' : 'p-2'
            }`}
            aria-label={getNextLabel()}
            title={getNextLabel()}
        >
            {getIcon()}
            {showLabel && (
                <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-600 dark:text-gray-400">
                        Theme:
                    </span>
                    <span className="text-sm font-medium text-gray-900 dark:text-white">
                        {getCurrentModeDisplay()}
                    </span>
                </div>
            )}
        </button>
    )
}
